import { Modal } from 'react-bootstrap';
import {
  FormProvider,
  type UseFormRegisterReturn,
  useForm,
} from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  PlannedResource,
  PlannedResourceRow,
  useUpdatePlannedResource,
} from 'components/IAP/hooks/assignmentListHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';

type ResourceFormData = PlannedResource & {
  resourceLeader: string;
  resourceName: string;
};

const AssignmentCardResourceAttachmentDialog = ({
  onClose,
  data: resourceAttachmentData,
}: {
  onClose: () => void;
  data: PlannedResourceRow;
}) => {
  const { mutate: update } = useUpdatePlannedResource(
    resourceAttachmentData.id
  );

  const onSubmit = (data: ResourceFormData) => {
    update({
      id: resourceAttachmentData.id,
      name: data.resourceName,
      numberOfPersons: resourceAttachmentData.numberOfPersons,
      contactInfo: resourceAttachmentData.contactInfo,
      reportingInfo: resourceAttachmentData.reportingInfo,
      has204a: resourceAttachmentData.has204a, // this is on purpose. We don't want to accidentally change whether this resource has a 204a form attached.
      leader: data.resourceLeader, // this is on purpose. This field is shared between two forms. We don't want to override the original.
      resourceBranch: data.resourceBranch,
      resourceDivision: data.resourceDivision,
      resourceGroup: data.resourceGroup,
      resourceStrikeTeam: data.resourceStrikeTeam,
      resourceTaskForce: data.resourceTaskForce,
      resourceAssignmentLocation: data.resourceAssignmentLocation,
      resourceSpecialInstructions: data.resourceSpecialInstructions,
      resourceSafetyPlanLocation: data.resourceSafetyPlanLocation,
    });
    onClose();
  };

  const formMethods = useForm<ResourceFormData>({
    mode: 'onChange',
    defaultValues: {
      resourceName: resourceAttachmentData.name || '', // this is on purpose. We don't want to accidentally send a new name for the resource.
      resourceLeader: resourceAttachmentData.leader || '', // this is on purpose. This field is shared between two forms. We don't want to override the original.
      resourceBranch: resourceAttachmentData.resourceBranch || '',
      resourceDivision: resourceAttachmentData.resourceDivision || '',
      resourceGroup: resourceAttachmentData.resourceGroup || '',
      resourceStrikeTeam: resourceAttachmentData.resourceStrikeTeam || '',
      resourceTaskForce: resourceAttachmentData.resourceTaskForce || '',
      resourceAssignmentLocation:
        resourceAttachmentData.resourceAssignmentLocation || '',
      resourceSpecialInstructions:
        resourceAttachmentData.resourceSpecialInstructions || '',
      resourceSafetyPlanLocation:
        resourceAttachmentData.resourceSafetyPlanLocation || '',
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;

  const { exportPlannedResourceAsForm } = useIcsFormPdfPreview();

  const handleExport = () => {
    if (resourceAttachmentData.walaId) {
      void exportPlannedResourceAsForm({
        walaId: resourceAttachmentData.walaId,
        plannedResourceId: resourceAttachmentData.id,
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Modal
        show={true}
        onHide={onClose}
        centered
        size="lg"
        backdrop={true}
        keyboard={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-start p-2">
              <div>
                <h4 className="m-0">Assignment List Attachment ICS-204A</h4>
                <h6 className="text-muted fst-italic mt-0">
                  {resourceAttachmentData.name}
                </h6>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={
                  !resourceAttachmentData.has204a ||
                  !resourceAttachmentData.walaId
                }
              >
                Preview PDF of 204A Form
              </Button>
            </div>
            <div className="tw:grid tw:grid-cols-3 tw:gap-4">
              <ResourceTextField
                label="Branch"
                inputProps={register('resourceBranch')}
              />
              <ResourceTextField
                label="Division"
                inputProps={register('resourceDivision')}
              />
              <ResourceTextField
                label="Group"
                inputProps={register('resourceGroup')}
              />
            </div>
            <div className="tw:mt-4 tw:grid tw:grid-cols-3 tw:gap-4">
              <ResourceTextField
                label="Strike Team"
                inputProps={register('resourceStrikeTeam')}
              />
              <ResourceTextField
                label="Task Force"
                inputProps={register('resourceTaskForce')}
              />
              <ResourceTextField
                label="Resource"
                inputProps={register('resourceName')}
                disabled
              />
            </div>
            <div className="tw:mt-4 tw:grid tw:grid-cols-2 tw:gap-4">
              <ResourceTextField
                label="Leader"
                inputProps={register('resourceLeader')}
              />
              <ResourceTextField
                label="Assignment Location"
                inputProps={register('resourceAssignmentLocation')}
              />
            </div>
            <div className="tw:mt-4 tw:space-y-2">
              <label
                className="tw:text-sm tw:font-medium tw:text-foreground"
                htmlFor="resourceSpecialInstructions"
              >
                Work Assignment Special Instructions
              </label>
              <Textarea
                id="resourceSpecialInstructions"
                aria-invalid={!!errors.resourceSpecialInstructions}
                {...register('resourceSpecialInstructions', {
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                })}
              />
              {errors.resourceSpecialInstructions && (
                <p className="tw:text-sm tw:text-destructive">
                  {errors.resourceSpecialInstructions.message}
                </p>
              )}
            </div>
            <div className="tw:mt-4">
              <ResourceTextField
                label="Site Safety Plan Location"
                inputProps={register('resourceSafetyPlanLocation')}
              />
            </div>

            <div className="tw:mt-6 tw:flex tw:justify-start tw:gap-2">
              <Button type="submit">Save</Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
};

function ResourceTextField({
  label,
  inputProps,
  disabled = false,
}: {
  label: string;
  inputProps: UseFormRegisterReturn;
  disabled?: boolean;
}) {
  const id = inputProps.name;

  return (
    <div className="tw:space-y-2">
      <label
        className="tw:text-sm tw:font-medium tw:text-foreground"
        htmlFor={id}
      >
        {label}
      </label>
      <Input id={id} disabled={disabled} {...inputProps} />
    </div>
  );
}

export default AssignmentCardResourceAttachmentDialog;
